import { Link, useLocation } from "react-router-dom";
import { Facebook, Instagram, Youtube, Twitter, Music2, Mail, CalendarHeart } from "lucide-react";
import { SOCIALS, EMAIL, EMAIL_MAILTO } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";

const ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  tiktok: Music2,
  x: Twitter,
  youtube: Youtube,
};

export const Footer = () => {
  const { t, to } = useLang();
  const f = t.footer;
  const { pathname } = useLocation();
  const showCta = !pathname.replace(/\/+$/, "").endsWith("/about");

  return (
    <footer data-testid="site-footer" className="bg-cream text-espresso border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 sm:py-20">
        {showCta && (
        <div data-testid="footer-cta" className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 pb-14 border-b border-espresso/10">
          <div className="max-w-xl">
            <p className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold mb-4">{f.kicker}</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight text-balance">
              {f.titlePre} <em className="text-terracotta">{f.titleEm}</em> {f.titlePost}
            </h2>
          </div>
          <Link
            to={to("/booking")}
            data-testid="footer-book-discovery-btn"
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-terracotta text-linen px-8 py-4 text-base font-semibold transition-[background-color,transform] duration-200 hover:bg-terracotta-dark [@media(hover:hover)]:hover:-translate-y-0.5"
          >
            <CalendarHeart className="w-5 h-5" />
            {f.cta}
          </Link>
        </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 py-12">
          <div>
            <p className="font-serif text-2xl font-semibold mb-1 text-[#6b7c7a]">Natalia</p>
            <p className="text-[11px] uppercase tracking-[0.22em] text-taupe font-semibold mb-4">Life Coach</p>
            <p className="text-sm text-clay leading-relaxed max-w-xs">{f.tagline}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-taupe font-semibold mb-4">{f.explore}</p>
            <ul className="space-y-2.5 text-sm">
              {[
                [f.links.home, "/", "footer-link-home"],
                [f.links.services, "/services", "footer-link-services"],
                [f.links.booking, "/booking", "footer-link-booking"],
                [f.links.payments, "/payments", "footer-link-payments"],
                [f.links.about, "/about", "footer-link-about"],
                [f.links.testimonials, "/testimonials", "footer-link-testimonials"],
              ].map(([label, path, id]) => (
                <li key={id}>
                  <Link to={to(path)} data-testid={id} className="text-clay hover:text-terracotta transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-taupe font-semibold mb-4">{f.connect}</p>
            <a
              href={EMAIL_MAILTO}
              data-testid="footer-email-link"
              className="inline-flex items-center gap-2 text-sm text-clay hover:text-terracotta transition-colors mb-5"
            >
              <Mail className="w-4 h-4" /> {EMAIL}
            </a>
            <div className="flex items-center gap-3">
              {SOCIALS.map((s) => {
                const Icon = ICONS[s.id];
                return (
                  <a
                    key={s.id}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-social-${s.id}`}
                    aria-label={s.label}
                    className="w-10 h-10 rounded-full border border-espresso/15 flex items-center justify-center text-clay hover:text-terracotta hover:border-terracotta/40 transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
            <p className="text-xs text-taupe mt-5">{f.creds}</p>
          </div>
        </div>

        <div className="pt-8 border-t border-espresso/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-taupe">{f.copyright}</p>
          <p className="text-xs text-taupe">{f.note}</p>
        </div>
      </div>
    </footer>
  );
};
